import React from "react";
import { Container, Card, Row, Col, Table } from "react-bootstrap";
import { BookOpen, CheckCircle, XCircle } from "react-feather";
import "./ReservasCanchas.css";

function ExplicacionReservas() {
  return (
    <Container fluid className="reservas-canchas-container">
      <Row className="mb-4">
        <Col>
          <h2 className="page-title">
            <BookOpen size={32} className="me-2" />
            Explicación Técnica - Sistema de Reservas de Canchas
          </h2>
        </Col>
      </Row>

      {/* Introducción */}
      <Card className="mb-4">
        <Card.Header className="bg-info text-white">
          <h4 className="mb-0">Objetivo del Sistema</h4>
        </Card.Header>
        <Card.Body>
          <p>
            El sistema de reservas está diseñado para <strong>optimizar el uso de las canchas</strong> y
            garantizar que todos los socios puedan encontrar horarios disponibles. Para lograrlo,
            implementa reglas que <strong>evitan la fragmentación de horarios</strong> en bloques
            que no pueden ser utilizados eficientemente.
          </p>
        </Card.Body>
      </Card>

      {/* Tipos de Reserva */}
      <Card className="mb-4">
        <Card.Header className="bg-primary text-white">
          <h4 className="mb-0">Tipos de Reserva</h4>
        </Card.Header>
        <Card.Body>
          <p>
            El sistema soporta dos tipos de canchas con <strong>lógicas de particionamiento diferentes</strong>:
          </p>
          <Row>
            <Col md={6}>
              <div className="alert alert-primary">
                <h5>🎾 Canchas de Tenis</h5>
                <p className="mb-2">Tipos de juego disponibles:</p>
                <Table bordered size="sm">
                  <thead>
                    <tr>
                      <th>Tipo</th>
                      <th>Duración</th>
                      <th>Slots</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Singles</td>
                      <td>60 min</td>
                      <td className="text-center">4</td>
                    </tr>
                    <tr className="table-warning">
                      <td>Singles 45 *</td>
                      <td>45 min</td>
                      <td className="text-center">3</td>
                    </tr>
                    <tr>
                      <td>Dobles 60</td>
                      <td>60 min</td>
                      <td className="text-center">4</td>
                    </tr>
                    <tr>
                      <td>Dobles 75</td>
                      <td>75 min</td>
                      <td className="text-center">5</td>
                    </tr>
                  </tbody>
                </Table>
                <p className="mb-0 mt-2">
                  <strong>Particionamiento:</strong> Múltiplos de 4, 5 y combinaciones
                </p>
                <p className="mb-0 mt-2">
                  <small className="text-warning">
                    * Singles 45 tiene reglas especiales (ver más abajo)
                  </small>
                </p>
              </div>
            </Col>
            <Col md={6}>
              <div className="alert alert-success">
                <h5>🏸 Canchas de Pádel</h5>
                <p className="mb-2">Tipos de juego disponibles:</p>
                <Table bordered size="sm">
                  <thead>
                    <tr>
                      <th>Tipo</th>
                      <th>Duración</th>
                      <th>Slots</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Singles</td>
                      <td>75 min</td>
                      <td className="text-center">5</td>
                    </tr>
                    <tr>
                      <td>Dobles</td>
                      <td>75 min</td>
                      <td className="text-center">5</td>
                    </tr>
                  </tbody>
                </Table>
                <p className="mb-0 mt-2">
                  <strong>Particionamiento:</strong> Solo múltiplos de 5
                </p>
              </div>
            </Col>
          </Row>
          <p className="mt-3 mb-0">
            <small className="text-muted">
              * Cada slot representa 15 minutos de tiempo
            </small>
          </p>
        </Card.Body>
      </Card>

      {/* Regla Principal */}
      <Card className="mb-4">
        <Card.Header className="bg-success text-white">
          <h4 className="mb-0">
            <CheckCircle size={20} className="me-2" />
            Regla Principal: Particionamiento Completo
          </h4>
        </Card.Header>
        <Card.Body>
          <p>
            Un slot puede marcarse como <strong style={{color: "#28a745"}}>inicio permitido de reserva (verde oscuro)</strong> según
            reglas que dependen del tipo de cancha:
          </p>

          <div className="alert alert-success mt-3">
            <strong>🎾 Tenis — Antes (siempre) y Después (condicional):</strong> Al concretarse el turno,
            el bloque libre <strong>ANTERIOR</strong> siempre debe poder particionarse en combinaciones válidas de 4 y 5 slots.
            El bloque libre <strong>POSTERIOR</strong> solo se controla cuando hay una <strong>pared con actividad posterior</strong> (ver detalle abajo).
          </div>

          <div className="alert alert-secondary mt-3">
            <strong>🧱 ¿Cuándo se controla el bloque POSTERIOR? (solo Tenis)</strong>
            <p className="mt-2 mb-2">
              Después de los slots libres que quedan tras la reserva, se mira qué hay al final de ese bloque libre:
            </p>
            <ul className="mb-0">
              <li><strong>Otra reserva (OCUPADO):</strong> Hay pared con actividad → <strong>SÍ se controla</strong> el particionamiento del bloque posterior.</li>
              <li><strong>Bloqueo seguido de más actividad</strong> (slots disponibles u ocupados después del bloqueo): Hay actividad posterior → <strong>SÍ se controla</strong>.</li>
              <li><strong>Fin del día</strong> (no hay más slots): No hay pared que atrape los slots → <strong>NO se controla</strong> (se considera particionable).</li>
              <li><strong>Bloqueo seguido solo de más bloqueos hasta el final:</strong> No hay actividad posterior → <strong>NO se controla</strong> (se considera particionable).</li>
            </ul>
          </div>

          <div className="alert alert-success mt-3">
            <strong>🏸 Pádel — Principalmente hacia atrás:</strong> Se verifica que el bloque libre <strong>ANTERIOR</strong> sea
            0 o múltiplo de 5. Como Pádel tiene una sola duración (5 módulos = 75 min),
            los slots se van llenando desde el borde de cada bloque libre, como fichas de dominó.
            Además, se permite reservar <strong>pegado al final</strong> del bloque libre (0 slots después),
            para no obligar al socio a un único horario de inicio cuando el bloque no es múltiplo de 5.
          </div>

          <div className="alert alert-secondary mt-3">
            <strong>🕐 Horarios pasados (solo al ver la fecha de hoy):</strong> Cuando se consulta la fecha actual,
            los slots cuyo horario de inicio ya pasó se consideran <strong>no disponibles</strong> a efectos del conteo
            de bloques libres. Es decir, un horario pasado actúa como límite de la misma forma que lo haría una reserva
            o un bloqueo. Esto significa que el primer slot futuro disponible puede quedar "pegado" al horario pasado,
            habilitando la excepción de pegadas si corresponde.
          </div>

          <h5 className="mt-4">🎾 Combinaciones Válidas para TENIS:</h5>
          <ul>
            <li>
              <strong>Múltiplos de 4</strong> (4, 8, 12, 16, 20, 24...):
              <span className="text-muted"> → Solo Singles 60min o Dobles 60min</span>
            </li>
            <li>
              <strong>Múltiplos de 5</strong> (5, 10, 15, 20, 25, 30...):
              <span className="text-muted"> → Solo Dobles 75min</span>
            </li>
            <li>
              <strong>Combinaciones mixtas de 4s y 5s</strong>:
              <span className="text-muted"> → Cualquier combinación de turnos de 60min (4 slots) y 75min (5 slots)</span>
              <ul className="mt-2">
                <li>9 slots = 1×Singles + 1×Dobles75 (4 + 5)</li>
                <li>13 slots = 2×Singles + 1×Dobles75 (8 + 5)</li>
                <li>14 slots = 1×Singles + 2×Dobles75 (4 + 10)</li>
                <li>17 slots = 3×Singles + 1×Dobles75 (12 + 5)</li>
                <li>18 slots = 2×Singles + 2×Dobles75 (8 + 10)</li>
                <li>Y así sucesivamente...</li>
              </ul>
            </li>
          </ul>

          <div className="alert alert-info mt-3">
            <strong>ℹ️ Flexibilidad Tenis:</strong> El sistema acepta <strong>cualquier combinación</strong> de turnos de 4 y 5 slots,
            permitiendo un aprovechamiento óptimo de todos los bloques horarios disponibles.
          </div>

          <h5 className="mt-4">🏸 Combinaciones Válidas para PÁDEL:</h5>
          <ul>
            <li>
              <strong>Solo múltiplos de 5</strong> (5, 10, 15, 20, 25, 30...):
              <span className="text-muted"> → Turnos de 75min (Singles o Dobles)</span>
            </li>
          </ul>

          <div className="alert alert-success mt-3">
            <strong>ℹ️ Simplicidad Pádel:</strong> La lógica de Pádel verifica que los slots
            libres <strong>antes</strong> de la reserva sean 0 o múltiplo de 5. Además permite reservar
            cuando los slots libres <strong>después</strong> son 0 (pegada al final del bloque).
          </div>

          <h5 className="mt-4">Ejemplos - TENIS:</h5>
          <Table bordered size="sm">
            <thead className="table-light">
              <tr>
                <th>Slots Disponibles</th>
                <th>¿Particionable?</th>
                <th>Explicación</th>
              </tr>
            </thead>
            <tbody>
              <tr className="table-success">
                <td>4 slots</td>
                <td className="text-center"><CheckCircle size={18} color="green" /></td>
                <td>Múltiplo de 4 → 1 turno de Singles o Dobles 60</td>
              </tr>
              <tr className="table-success">
                <td>5 slots</td>
                <td className="text-center"><CheckCircle size={18} color="green" /></td>
                <td>Múltiplo de 5 → 1 turno de Dobles 75</td>
              </tr>
              <tr className="table-success">
                <td>8 slots</td>
                <td className="text-center"><CheckCircle size={18} color="green" /></td>
                <td>Múltiplo de 4 → 2 turnos de Singles o Dobles 60</td>
              </tr>
              <tr className="table-success">
                <td>9 slots</td>
                <td className="text-center"><CheckCircle size={18} color="green" /></td>
                <td>Combinación mixta → 1×Singles + 1×Dobles75 (4 + 5)</td>
              </tr>
              <tr className="table-success">
                <td>13 slots</td>
                <td className="text-center"><CheckCircle size={18} color="green" /></td>
                <td>Combinación mixta → 2×Singles + 1×Dobles75 (8 + 5)</td>
              </tr>
              <tr className="table-success">
                <td>14 slots</td>
                <td className="text-center"><CheckCircle size={18} color="green" /></td>
                <td>Combinación mixta → 1×Singles + 2×Dobles75 (4 + 10)</td>
              </tr>
              <tr className="table-success">
                <td>18 slots</td>
                <td className="text-center"><CheckCircle size={18} color="green" /></td>
                <td>Combinación mixta → 2×Singles + 2×Dobles75 (8 + 10)</td>
              </tr>
              <tr className="table-danger">
                <td>6 slots</td>
                <td className="text-center"><XCircle size={18} color="red" /></td>
                <td>No puede formarse con combinaciones de 4s y 5s (Tenis)</td>
              </tr>
              <tr className="table-danger">
                <td>7 slots</td>
                <td className="text-center"><XCircle size={18} color="red" /></td>
                <td>No puede formarse con combinaciones de 4s y 5s (Tenis)</td>
              </tr>
              <tr className="table-danger">
                <td>11 slots</td>
                <td className="text-center"><XCircle size={18} color="red" /></td>
                <td>No puede formarse con combinaciones de 4s y 5s (Tenis)</td>
              </tr>
            </tbody>
          </Table>

          <h5 className="mt-4">Ejemplo - Control posterior condicional (Tenis):</h5>
          <Table bordered size="sm">
            <thead className="table-light">
              <tr>
                <th>Escenario</th>
                <th>Qué hay después de los slots libres</th>
                <th>¿Se controla "después"?</th>
                <th>Resultado</th>
              </tr>
            </thead>
            <tbody>
              <tr className="table-success">
                <td>3 slots libres → fin del día</td>
                <td>Nada (fin del array)</td>
                <td>No</td>
                <td>Verde oscuro — no hay hueco atrapado</td>
              </tr>
              <tr className="table-success">
                <td>3 slots libres → bloqueo → bloqueo → fin</td>
                <td>Bloqueos hasta el final</td>
                <td>No</td>
                <td>Verde oscuro — sin actividad posterior</td>
              </tr>
              <tr className="table-warning">
                <td>3 slots libres → reserva</td>
                <td>Reserva (OCUPADO)</td>
                <td>Sí</td>
                <td>3 no es particionable → verde claro (necesita pegada)</td>
              </tr>
              <tr className="table-warning">
                <td>3 slots libres → bloqueo → disponibles</td>
                <td>Bloqueo + actividad posterior</td>
                <td>Sí</td>
                <td>3 no es particionable → verde claro (necesita pegada)</td>
              </tr>
            </tbody>
          </Table>

          <h5 className="mt-4">Ejemplos - PÁDEL:</h5>
          <Table bordered size="sm">
            <thead className="table-light">
              <tr>
                <th>Slots Disponibles</th>
                <th>¿Particionable?</th>
                <th>Explicación</th>
              </tr>
            </thead>
            <tbody>
              <tr className="table-success">
                <td>5 slots</td>
                <td className="text-center"><CheckCircle size={18} color="green" /></td>
                <td>Múltiplo de 5 → 1 turno de 75 min</td>
              </tr>
              <tr className="table-success">
                <td>10 slots</td>
                <td className="text-center"><CheckCircle size={18} color="green" /></td>
                <td>Múltiplo de 5 → 2 turnos de 75 min</td>
              </tr>
              <tr className="table-success">
                <td>15 slots</td>
                <td className="text-center"><CheckCircle size={18} color="green" /></td>
                <td>Múltiplo de 5 → 3 turnos de 75 min</td>
              </tr>
              <tr className="table-success">
                <td>20 slots</td>
                <td className="text-center"><CheckCircle size={18} color="green" /></td>
                <td>Múltiplo de 5 → 4 turnos de 75 min</td>
              </tr>
              <tr className="table-success">
                <td>60 slots</td>
                <td className="text-center"><CheckCircle size={18} color="green" /></td>
                <td>Múltiplo de 5 → 12 turnos de 75 min (horario 07:30-22:30)</td>
              </tr>
              <tr className="table-danger">
                <td>4 slots</td>
                <td className="text-center"><XCircle size={18} color="red" /></td>
                <td>No es múltiplo de 5 (Pádel)</td>
              </tr>
              <tr className="table-danger">
                <td>6 slots</td>
                <td className="text-center"><XCircle size={18} color="red" /></td>
                <td>No es múltiplo de 5 (Pádel)</td>
              </tr>
              <tr className="table-danger">
                <td>7 slots</td>
                <td className="text-center"><XCircle size={18} color="red" /></td>
                <td>No es múltiplo de 5 (Pádel)</td>
              </tr>
              <tr className="table-danger">
                <td>61 slots</td>
                <td className="text-center"><XCircle size={18} color="red" /></td>
                <td>No es múltiplo de 5 → Crear bloqueo en último slot para tener 60</td>
              </tr>
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Excepción */}
      <Card className="mb-4">
        <Card.Header className="bg-warning text-dark">
          <h4 className="mb-0">Excepción: Reservas "Pegadas"</h4>
        </Card.Header>
        <Card.Body>
          <div className="alert alert-primary mt-3">
            <strong>🎾 Tenis:</strong> Si un bloque libre <strong>NO puede particionarse</strong> en combinaciones de 4s y 5s,
            se permite iniciar una reserva <strong>solo</strong> si cumple al menos una de estas condiciones:
            <ul className="mt-2 mb-0">
              <li>Quedan <strong>0 slots libres ANTES</strong> del turno (pegado al inicio del día, a otra reserva, a un bloqueo, o a un horario que ya pasó)</li>
              <li>Quedan <strong>0 slots libres DESPUÉS</strong> del turno (pegado al fin del día, a otra reserva, o a un bloqueo)</li>
            </ul>
          </div>

          <div className="alert alert-success mt-3">
            <strong>🏸 Pádel:</strong> Si el bloque libre anterior NO es 0 ni múltiplo de 5 (regla principal no se cumple),
            se permite iniciar la reserva si quedan <strong>0 slots libres DESPUÉS</strong> del turno (pegado al final del bloque).
            <p className="mt-2 mb-0">
              <strong>Ejemplo:</strong> Última reserva termina a las 21:00, cancha cierra a las 23:00 (8 slots libres).
              Sin pegadas, solo se puede iniciar a las 21:00. Con pegadas, también se puede iniciar a las 21:30
              (termina justo a las 23:00, pegada al final).
            </p>
          </div>

          <div className="alert alert-warning mt-3">
            <strong>⚠️ Importante:</strong> La reserva debe quedar <strong>pegada</strong> a algún borde.
            <strong>NO</strong> se permiten inicios que dejen huecos no particionables a ambos lados.
          </div>

          <div className="alert alert-info mt-3">
            <strong>ℹ️ Razón de esta excepción:</strong> primero debemos considerar los siguientes dos puntos. 
            <br/>
1 - Cuando un usuario hace clic en confirmar reserva se vuelven a validar restricciones de reservas vigentes, fechas abiertas, y solapamientos con otras reservas pero no se vuelven a validar huecos óptimos para no frustrar la experiencia del usuario que ya ingresó todos los datos. 
            <br/>
2 - Cuando un usuario del tipo Gestor de Reservas crea un bloqueo no se validan la optimización de huecos para permitirle o no crear el bloqueo. Tiene libertad para hacerlo según las necesidades del club. 
<br/>
<br/>

Por las consideraciones anteriores pueden quedar bloques no particionables en Tenis (6, 7, 11 slots, etc.). Esta excepción permite aprovechar esos bloques siempre que la reserva quede pegada, evitando fragmentar aún más el horario disponible.
          </div>

          <h5 className="mt-4">Ejemplo Práctico: Bloque no particionable</h5>
          <p>
            Hay un bloque de 7 slots libres (no particionable) entre dos reservas y queremos reservar Dobles 75min (5 slots):
          </p>
          <Table bordered size="sm">
            <thead className="table-light">
              <tr>
                <th>Posición de Reserva</th>
                <th>Slots Antes</th>
                <th>Slots Después</th>
                <th>¿Particionables?</th>
                <th>¿Permitido?</th>
                <th>Motivo</th>
              </tr>
            </thead>
            <tbody>
              <tr className="table-success">
                <td>Al inicio (pegado)</td>
                <td>0</td>
                <td>2</td>
                <td>0 es particionable, 2 NO</td>
                <td className="text-center"><CheckCircle size={18} color="green" /></td>
                <td>Pegado al inicio (0 antes) → EXCEPCIÓN aplica ✓</td>
              </tr>
              <tr className="table-danger">
                <td>En medio</td>
                <td>1</td>
                <td>1</td>
                <td>Ambos NO particionables</td>
                <td className="text-center"><XCircle size={18} color="red" /></td>
                <td>Deja huecos no particionables a ambos lados → NO permitido ✗</td>
              </tr>
              <tr className="table-success">
                <td>Al final (pegado)</td>
                <td>2</td>
                <td>0</td>
                <td>2 NO, 0 es particionable</td>
                <td className="text-center"><CheckCircle size={18} color="green" /></td>
                <td>Pegado al final (0 después) → EXCEPCIÓN aplica ✓</td>
              </tr>
            </tbody>
          </Table>

          <div className="alert alert-success mt-3">
            <strong>✓ Conclusión:</strong> Cuando hay bloques no particionables, solo se permite
            reservar en los extremos (pegado), nunca en el medio.
          </div>
        </Card.Body>
      </Card>

      {/* Horario Off-Peak */}
      <Card className="mb-4">
        <Card.Header className="bg-info text-white">
          <h4 className="mb-0">Horario Off-Peak (Fin de Hora Pico)</h4>
        </Card.Header>
        <Card.Body>
          <p>
            A partir de una hora configurable (<strong>Fin de Hora Pico</strong>, por defecto <strong>20:00</strong>),
            las reglas de particionamiento se <strong>desactivan completamente</strong>. Esto significa que cualquier
            slot disponible en horario off-peak puede iniciar una reserva siempre que haya suficientes slots
            consecutivos disponibles para el tipo de juego elegido.
          </p>

          <div className="alert alert-info">
            <strong>ℹ️ ¿Por qué existe esta regla?</strong>
            <p className="mb-0">
              En horarios de baja demanda (generalmente a la noche), las reglas de particionamiento pueden
              bloquear todos los slots disponibles si el bloque libre no se divide limpiamente en combinaciones
              válidas. Esto genera que nadie pueda reservar aunque haya cancha libre. Al desactivar el
              particionamiento en off-peak, se prioriza que los socios puedan jugar por sobre la optimización
              de huecos.
            </p>
          </div>

          <h5 className="mt-4">Flujo de validación con Off-Peak:</h5>
          <ol>
            <li>¿El slot está DISPONIBLE? → Si no, no se puede reservar</li>
            <li>¿El horario ya pasó? → Si pasó, no se puede reservar</li>
            <li>¿Hay suficientes slots consecutivos disponibles para el tipo de juego? → Si no entran, no se puede reservar</li>
            <li className="mt-2"><strong>¿El slot es off-peak (hora ≥ Fin de Hora Pico)?</strong> → Si es off-peak, <strong>se permite directamente</strong> sin chequear particionamiento</li>
            <li>¿Es fecha presencial? → Se aplica regla presencial (ver sección Fechas Presenciales)</li>
            <li>Si es horario pico y no presencial → Se aplican las reglas de particionamiento normales</li>
          </ol>

          <div className="alert alert-success mt-3">
            <strong>✓ Aplica para todos los tipos de juego:</strong> En horario off-peak, <strong>cualquier</strong> tipo
            de juego queda habilitado sin restricciones de particionamiento, incluyendo <strong>Singles 45</strong> (que
            en horario pico solo se permite en bloques de exactamente 3 slots encerrados).
          </div>

          <div className="alert alert-warning mt-3">
            <strong>⚠️ Importante:</strong> La validación de slots consecutivos suficientes (<strong>SLOTS_INSUFICIENTES</strong>)
            sigue activa en off-peak. Por ejemplo, un Dobles 75min (5 slots) no puede iniciar en el anteúltimo
            slot del día porque no entran los 5 módulos necesarios. Lo que se omite es únicamente la lógica de
            particionamiento (huecos particionables, excepción de pegadas, regla de encerrado de Singles 45, etc.).
          </div>

          <div className="alert alert-secondary mt-3">
            <strong>⚙️ Configuración:</strong> La hora de fin de pico se configura desde{" "}
            <strong>Configuración Global → Reservas de Canchas → Horarios de Canchas → Fin de Hora Pico</strong>.
          </div>
        </Card.Body>
      </Card>

      {/* Fechas Presenciales */}
      <Card className="mb-4">
        <Card.Header className="bg-dark text-white">
          <h4 className="mb-0">Fechas Presenciales</h4>
        </Card.Header>
        <Card.Body>
          <p>
            Cuando una fecha está marcada como <strong>presencial</strong>, las reglas de particionamiento
            se reemplazan por una regla más restrictiva: cada reserva debe quedar <strong>pegada al borde
            anterior</strong> (es decir, solo se puede reservar en el primer slot disponible de cada cancha).
          </p>

          <div className="alert alert-primary">
            <strong>Regla Presencial:</strong> Solo se permite iniciar una reserva si hay{" "}
            <strong>0 slots libres ANTES</strong> del turno. Esto obliga a que las canchas se vayan
            llenando de arriba hacia abajo de forma pareja.
          </div>

          <div className="alert alert-secondary mt-3">
            <strong>¿Qué cuenta como "borde anterior"?</strong>
            <ul className="mb-0 mt-2">
              <li><strong>Inicio del día</strong> (primer slot de la cancha)</li>
              <li><strong>Otra reserva</strong> (slot OCUPADO inmediatamente antes)</li>
              <li><strong>Un bloqueo</strong> (slot BLOQUEADO inmediatamente antes), <strong>solo si</strong> mirando hacia atrás
                entre el horario pasado (o inicio del día) y el bloqueo <strong>no hay ningún slot disponible</strong> (verde claro o verde oscuro).
                Si hay slots libres antes del bloqueo, este no cuenta como borde y la reserva no se permite ahí.</li>
              <li><strong>Horario pasado</strong> (la línea azul del horario actual, solo aplica al ver el día de hoy)</li>
            </ul>
          </div>

          <h5 className="mt-4">Flujo de validación en fecha presencial:</h5>
          <ol>
            <li>¿El slot está DISPONIBLE? → Si no, no se puede reservar</li>
            <li>¿El horario ya pasó? → Si pasó, no se puede reservar</li>
            <li>¿Hay suficientes slots consecutivos disponibles para el tipo de juego? → Si no entran, no se puede reservar</li>
            <li><strong>¿El slot es off-peak?</strong> → Si es off-peak, <strong>se permite directamente</strong> (off-peak sigue funcionando igual)</li>
            <li><strong>¿Es fecha presencial?</strong> → Si hay 0 slots libres antes, se permite. Si no, <strong>no se permite</strong></li>
          </ol>

          <div className="alert alert-info mt-3">
            <strong>ℹ️ Off-Peak sigue vigente:</strong> Si la fecha es presencial pero el slot cae en horario
            off-peak (por defecto a partir de las 20:00), las restricciones presenciales{" "}
            <strong>no aplican</strong> y se permite reservar libremente como en cualquier horario off-peak.
          </div>

          <h5 className="mt-4">Ejemplo Visual:</h5>
          <Table bordered size="sm">
            <thead className="table-light">
              <tr>
                <th>Escenario</th>
                <th>Slots libres antes</th>
                <th>¿Permitido?</th>
                <th>Motivo</th>
              </tr>
            </thead>
            <tbody>
              <tr className="table-success">
                <td>Primer slot del día (08:00)</td>
                <td>0</td>
                <td className="text-center"><CheckCircle size={18} color="green" /></td>
                <td>Pegado al inicio del día</td>
              </tr>
              <tr className="table-success">
                <td>Inmediatamente después de otra reserva</td>
                <td>0</td>
                <td className="text-center"><CheckCircle size={18} color="green" /></td>
                <td>Pegado a reserva anterior</td>
              </tr>
              <tr className="table-success">
                <td>Inmediatamente después de un bloqueo</td>
                <td>0</td>
                <td className="text-center"><CheckCircle size={18} color="green" /></td>
                <td>Pegado a bloqueo</td>
              </tr>
              <tr className="table-success">
                <td>Primer slot futuro del día de hoy</td>
                <td>0</td>
                <td className="text-center"><CheckCircle size={18} color="green" /></td>
                <td>Pegado al horario pasado (línea azul)</td>
              </tr>
              <tr className="table-danger">
                <td>Slot con huecos libres antes</td>
                <td>3</td>
                <td className="text-center"><XCircle size={18} color="red" /></td>
                <td>No está pegado al borde anterior</td>
              </tr>
            </tbody>
          </Table>

          <div className="alert alert-success mt-3">
            <strong>✓ Objetivo:</strong> Las fechas presenciales están pensadas para la reserva en mostrador.
            Al obligar a reservar siempre pegado al borde, las canchas se van ocupando de forma pareja y ordenada,
            facilitando la gestión presencial.
          </div>

          <div className="alert alert-warning mt-3">
            <strong>⚠️ Importante:</strong> Cuando una fecha es presencial, los socios{" "}
            <strong>no pueden</strong> hacer reservas online. Solo los administradores y gestores de reservas
            pueden crear reservas en fechas presenciales.
          </div>
        </Card.Body>
      </Card>

      {/* Caso Especial: Singles 45 */}
      <Card className="mb-4">
        <Card.Header className="bg-warning text-dark">
          <h4 className="mb-0">⚡ Caso Especial: Singles de 45 Minutos</h4>
        </Card.Header>
        <Card.Body>
          <p>
            <strong>Singles de 45 minutos</strong> es una modalidad especial que <strong>NO sigue las reglas de particionamiento estándar</strong> de tenis.
            Fue diseñada específicamente para aprovechar bloques de tiempo que quedarían vacíos.
          </p>

          <div className="alert alert-warning">
            <h5>🎯 Regla Única para Singles 45:</h5>
            <p className="mb-0">
              Esta modalidad <strong>SOLO está disponible</strong> cuando se cumplen <strong>TODAS</strong> estas condiciones:
            </p>
          </div>

          <div className="ms-3 mt-3">
            <h6>✓ Condiciones Obligatorias:</h6>
            <ol>
              <li className="mb-2">
                <strong>Exactamente 3 slots consecutivos disponibles</strong> (45 minutos)
              </li>
              <li className="mb-2">
                <strong>Completamente encerrados</strong> por alguna de estas situaciones:
                <ul className="mt-2">
                  <li>Otras reservas (Singles, Dobles 60, Dobles 75)</li>
                  <li>Bloqueos de cancha (mantenimiento, eventos, etc.)</li>
                  <li>Inicio o fin de jornada</li>
                </ul>
              </li>
              <li>
                <strong>0 slots disponibles ANTES</strong> Y <strong>0 slots disponibles DESPUÉS</strong>
              </li>
            </ol>
          </div>

          <div className="alert alert-info mt-4">
            <strong>ℹ️ ¿Por qué existe Singles 45?</strong>
            <p className="mb-2">
              Las reglas de particionamiento de tenis (múltiplos de 4 y 5) garantizan un uso óptimo de las canchas,
              pero pueden quedar bloques de 3 slots "atrapados" entre reservas o bloqueos.
            </p>
            <p className="mb-0">
              Singles 45 permite aprovechar estos bloques que de otra forma quedarían completamente desperdiciados,
              sin afectar las reglas generales de optimización.
            </p>
          </div>

          <h5 className="mt-4">Ejemplo Visual:</h5>
          <div className="p-3 bg-light border rounded">
            <div className="d-flex align-items-center gap-2 mb-3">
              <span className="text-muted">08:00</span>
              <div style={{width: "60px", height: "30px", backgroundColor: "#dc3545", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", color: "white"}}>
                Reserva
              </div>
              <div style={{width: "45px", height: "30px", backgroundColor: "#28a745", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", color: "white", border: "2px solid gold"}}>
                ✓ 3
              </div>
              <div style={{width: "75px", height: "30px", backgroundColor: "#dc3545", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", color: "white"}}>
                Reserva
              </div>
              <span className="text-muted">11:00</span>
            </div>
            <p className="mb-0">
              <CheckCircle size={16} color="green" className="me-2" />
              <strong>Permitido:</strong> 3 slots encerrados por reservas → Puede reservar Singles 45
            </p>
          </div>

          <div className="p-3 bg-light border rounded mt-3">
            <div className="d-flex align-items-center gap-2 mb-3">
              <span className="text-muted">08:00</span>
              <div style={{width: "60px", height: "30px", backgroundColor: "#dc3545", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", color: "white"}}>
                Reserva
              </div>
              <div style={{width: "15px", height: "30px", backgroundColor: "#d4edda", border: "1px solid #c3e6cb"}}></div>
              <div style={{width: "45px", height: "30px", backgroundColor: "#d4edda", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", border: "1px solid #c3e6cb"}}>
                ✗ 3
              </div>
              <div style={{width: "15px", height: "30px", backgroundColor: "#d4edda", border: "1px solid #c3e6cb"}}></div>
              <div style={{width: "75px", height: "30px", backgroundColor: "#dc3545", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", color: "white"}}>
                Reserva
              </div>
              <span className="text-muted">11:15</span>
            </div>
            <p className="mb-0">
              <XCircle size={16} color="red" className="me-2" />
              <strong>NO Permitido:</strong> Hay slots libres antes y después → No cumple regla de encerrado
            </p>
          </div>

          <div className="alert alert-success mt-4">
            <strong>✓ Resumen:</strong>
            <ul className="mb-0 mt-2">
              <li>Singles 45 <strong>NO participa</strong> en las reglas de particionamiento estándar de tenis</li>
              <li>Solo se activa cuando hay <strong>exactamente 3 slots encerrados</strong></li>
              <li>Permite <strong>aprovechar huecos</strong> que de otra forma serían inutilizables</li>
              <li>No afecta la optimización general del sistema</li>
            </ul>
          </div>
        </Card.Body>
      </Card>

      {/* Colores en la Matriz */}
      <Card className="mb-4">
        <Card.Header className="bg-secondary text-white">
          <h4 className="mb-0">Significado de los Colores en la Matriz</h4>
        </Card.Header>
        <Card.Body>
          <div className="d-flex flex-column gap-3">
            <div className="d-flex align-items-center gap-3">
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  backgroundColor: "#28a745",
                  borderRadius: "4px",
                }}
              ></div>
              <div>
                <strong>Verde Oscuro</strong>
                <p className="mb-0 text-muted">
                  Puede iniciar reserva aquí. Cumple la regla principal o la excepción.
                </p>
              </div>
            </div>

            <div className="d-flex align-items-center gap-3">
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  backgroundColor: "#d4edda",
                  borderRadius: "4px",
                  border: "1px solid #c3e6cb",
                }}
              ></div>
              <div>
                <strong>Verde Claro</strong>
                <p className="mb-0 text-muted">
                  Disponible pero NO puede iniciar reserva (dejaría huecos no utilizables).
                </p>
              </div>
            </div>

            <div className="d-flex align-items-center gap-3">
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  backgroundColor: "#dc3545",
                  borderRadius: "4px",
                }}
              ></div>
              <div>
                <strong>Rojo</strong>
                <p className="mb-0 text-muted">
                  Reservado desde otro usuario.
                </p>
              </div>
            </div>

            <div className="d-flex align-items-center gap-3">
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  backgroundColor: "#ffc107",
                  borderRadius: "4px",
                }}
              ></div>
              <div>
                <strong>Amarillo</strong>
                <p className="mb-0 text-muted">
                  Tu reserva.
                </p>
              </div>
            </div>

            <div className="d-flex align-items-center gap-3">
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  backgroundColor: "#6c757d",
                  borderRadius: "4px",
                }}
              ></div>
              <div>
                <strong>Gris</strong>
                <p className="mb-0 text-muted">
                  Bloqueado por el club.
                </p>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Beneficios */}
      <Card className="mb-4">
        <Card.Header className="bg-info text-white">
          <h4 className="mb-0">Beneficios del Sistema</h4>
        </Card.Header>
        <Card.Body>
          <ul>
            <li>
              <strong>Optimización del uso:</strong> Evita dejar huecos de tiempo que no pueden ser aprovechados.
            </li>
            <li>
              <strong>Equidad:</strong> Garantiza que todos los socios tengan oportunidad de reservar.
            </li>
            <li>
              <strong>Transparencia:</strong> Los colores indican claramente dónde se puede iniciar una reserva.
            </li>
            <li>
              <strong>Eficiencia:</strong> Maximiza la cantidad de turnos que se pueden concretar en el día.
            </li>
          </ul>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default ExplicacionReservas;
